import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import * as React from 'react';

import { cn } from '@apps/web/lib/utils';

/*
 * Admin table kit — Linear/Vercel-tight editorial rows.
 *
 * Hairline dividers only (no zebra striping). Column header row is
 * micro-caps on a translucent sticky surface so scrolling body content
 * fades behind it. Row hover / selected states use an ultra-light
 * Phoebe purple wash so a table full of them stays quiet while still
 * reading as branded. Numeric columns get right-alignment plus tabular
 * numerals via context so digits align vertically as data updates.
 *
 * Density flows from AdminTable through context:
 *   regular  — 40px rows (py-2.5), 13px body text
 *   compact  — 32px rows (py-1.5), 11px body text
 */

type AdminTableDensity = 'regular' | 'compact';

const AdminTableDensityContext =
    React.createContext<AdminTableDensity>('regular');

export function AdminTable({
    density = 'regular',
    className,
    containerClassName,
    children,
    ...props
}: React.ComponentProps<'table'> & {
    readonly density?: AdminTableDensity;
    readonly containerClassName?: string;
}) {
    return (
        <AdminTableDensityContext.Provider value={density}>
            <div
                data-slot="admin-table-container"
                className={cn('w-full overflow-x-auto', containerClassName)}
            >
                <table
                    data-slot="admin-table"
                    className={cn('text-admin-base w-full', className)}
                    {...props}
                >
                    {children}
                </table>
            </div>
        </AdminTableDensityContext.Provider>
    );
}

export function AdminTableHeader({
    className,
    ...props
}: React.ComponentProps<'thead'>) {
    // Sticky header sits above the scrolled body with a translucent
    // paper background + backdrop blur, so body text fades under it
    // instead of showing a hard band. Older browsers without
    // backdrop-filter support fall back to the 85% white fill alone,
    // which still reads as an elevated bar over the ink-on-paper body.
    return (
        <thead
            data-slot="admin-table-header"
            className={cn(
                'sticky top-0 z-10 bg-[rgba(255,255,255,0.85)] backdrop-blur-[8px]',
                className,
            )}
            {...props}
        />
    );
}

export function AdminTableBody({
    className,
    ...props
}: React.ComponentProps<'tbody'>) {
    return (
        <tbody data-slot="admin-table-body" className={className} {...props} />
    );
}

export function AdminTableRow({
    selected = false,
    className,
    ...props
}: React.ComponentProps<'tr'> & {
    readonly selected?: boolean;
}) {
    const density = React.useContext(AdminTableDensityContext);
    return (
        <tr
            data-slot="admin-table-row"
            data-selected={selected}
            className={cn(
                'border-admin-border border-b transition-[background-color] duration-[var(--admin-dur-fast)] ease-[var(--admin-ease-in-out)]',
                density === 'compact' ? 'h-8' : 'h-10',
                // Hover reads as a whisper of the Phoebe purple; the
                // selected state deepens the same wash so the two
                // interactive strengths ladder cleanly without
                // introducing a second hue. Both scale off
                // --admin-purple so any repalette flows through.
                selected
                    ? 'bg-admin-purple/[.06]'
                    : 'hover:bg-admin-purple/[.02]',
                className,
            )}
            {...props}
        />
    );
}

export function AdminTableHeadCell({
    numeric = false,
    className,
    ...props
}: React.ComponentProps<'th'> & {
    readonly numeric?: boolean;
}) {
    return (
        <th
            data-slot="admin-table-head-cell"
            className={cn(
                'text-admin-xs text-admin-ink-muted border-admin-border h-8 border-b px-3 text-left font-medium tracking-[0.06em] whitespace-nowrap uppercase first:pl-6 last:pr-6',
                numeric && 'text-right tabular-nums',
                className,
            )}
            {...props}
        />
    );
}

export type AdminTableSortDirection = 'asc' | 'desc';

export function AdminTableSortableHeadCell({
    active,
    direction,
    onSort,
    numeric = false,
    className,
    children,
    ...props
}: React.ComponentProps<'th'> & {
    readonly active: boolean;
    readonly direction: AdminTableSortDirection;
    readonly onSort: () => void;
    readonly numeric?: boolean;
}) {
    const Icon = active
        ? direction === 'asc'
            ? ArrowUp
            : ArrowDown
        : ArrowUpDown;
    return (
        <th
            data-slot="admin-table-head-cell"
            aria-sort={
                active
                    ? direction === 'asc'
                        ? 'ascending'
                        : 'descending'
                    : undefined
            }
            className={cn(
                'text-admin-xs text-admin-ink-muted border-admin-border h-8 border-b px-3 text-left font-medium tracking-[0.06em] whitespace-nowrap uppercase first:pl-6 last:pr-6',
                numeric && 'text-right tabular-nums',
                className,
            )}
            {...props}
        >
            {/* Tailwind preflight makes the button inherit the head cell's
                micro-caps typography, so only the interactive states need
                styling here. */}
            <button
                type="button"
                onClick={onSort}
                className={cn(
                    'hover:text-admin-ink inline-flex cursor-pointer items-center gap-1 uppercase transition-[color] duration-[var(--admin-dur-fast)]',
                    active && 'text-admin-ink',
                )}
            >
                {children}
                <Icon
                    aria-hidden="true"
                    className={cn(
                        'size-3 shrink-0',
                        !active && 'text-admin-ink-faint',
                    )}
                    strokeWidth={1.75}
                />
            </button>
        </th>
    );
}

export function AdminTableCell({
    numeric = false,
    className,
    ...props
}: React.ComponentProps<'td'> & {
    readonly numeric?: boolean;
}) {
    const density = React.useContext(AdminTableDensityContext);
    return (
        <td
            data-slot="admin-table-cell"
            className={cn(
                'text-admin-ink-secondary px-3 align-middle first:pl-6 last:pr-6',
                density === 'compact'
                    ? 'text-admin-xs py-1.5'
                    : 'text-admin-base py-2.5',
                numeric && 'text-right tabular-nums',
                className,
            )}
            {...props}
        />
    );
}
